/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Keyboard Shortcuts Configuration
 *
 * Centralized configuration for all keyboard shortcuts in the application.
 * Supports Mac (⌘) and Windows/Linux (Ctrl) modifiers.
 */

import type { LucideIcon } from "lucide-react";
import {
  Calculator,
  FolderOpen,
  Home,
  MessageSquare,
  Plus,
  Search,
  Settings,
  Users,
} from "lucide-react";

export interface KeyboardShortcut {
  /** Unique identifier for the shortcut */
  id: string;
  /** Display name for the shortcut */
  name: string;
  /** Description of what the shortcut does */
  description: string;
  /** Key combination (e.g., "mod+k" where mod = Cmd/Ctrl) */
  keys: string;
  /** Alternative key combination (optional) */
  altKeys?: string;
  /** Category for grouping in help modal */
  category: "navigation" | "actions" | "search" | "global";
  /** Icon to display next to shortcut */
  icon?: LucideIcon;
  /** Function to execute when shortcut is triggered */
  action: () => void;
  /** Whether the shortcut is enabled */
  enabled?: boolean;
}

/**
 * Convert modifier key notation to platform-specific display
 * @param keys - Key combination (e.g., "mod+k")
 * @returns Display string (e.g., "⌘K" on Mac, "Ctrl+K" on Windows)
 */
export function formatShortcutKeys(keys: string): string {
  const isMac =
    typeof window !== "undefined" &&
    navigator.platform.toUpperCase().indexOf("MAC") >= 0;

  return keys
    .split("+")
    .map((key) => {
      switch (key.toLowerCase()) {
        case "mod":
          return isMac ? "⌘" : "Ctrl";
        case "shift":
          return isMac ? "⇧" : "Shift";
        case "alt":
          return isMac ? "⌥" : "Alt";
        case "ctrl":
          return isMac ? "⌃" : "Ctrl";
        default:
          return key.toUpperCase();
      }
    })
    .join(isMac ? "" : "+");
}

/**
 * Check if a keyboard event matches a shortcut key combination
 * @param event - Keyboard event
 * @param keys - Key combination to match (e.g., "mod+k")
 * @returns Whether the event matches the keys
 */
export function matchesShortcut(event: KeyboardEvent, keys: string): boolean {
  if (!keys) return false;
  const parts = keys.toLowerCase().split("+");
  const key = parts[parts.length - 1];
  const modifiers = parts.slice(0, -1);
  const eventKey = typeof event.key === "string" ? event.key.toLowerCase() : "";

  if (!eventKey) {
    return false;
  }

  // Check if the key matches
  if (eventKey !== key) {
    return false;
  }

  // Check modifiers
  const isMac =
    typeof window !== "undefined" &&
    navigator.platform.toUpperCase().indexOf("MAC") >= 0;

  for (const modifier of modifiers) {
    switch (modifier) {
      case "mod":
        if (isMac) {
          if (!event.metaKey) return false;
        } else {
          if (!event.ctrlKey) return false;
        }
        break;
      case "shift":
        if (!event.shiftKey) return false;
        break;
      case "alt":
        if (!event.altKey) return false;
        break;
      case "ctrl":
        if (!event.ctrlKey) return false;
        break;
    }
  }

  return true;
}

/**
 * Global keyboard shortcuts configuration
 * These shortcuts work anywhere in the application
 */
export const globalShortcuts: Omit<KeyboardShortcut, "action">[] = [
  // Navigation Shortcuts
  {
    id: "goto-dashboard",
    name: "Go to Dashboard",
    description: "Navigate to the main dashboard",
    keys: "g+d",
    category: "navigation",
    icon: Home,
  },
  {
    id: "goto-projects",
    name: "Go to Projects",
    description: "Navigate to project management",
    keys: "g+p",
    category: "navigation",
    icon: FolderOpen,
  },
  {
    id: "goto-estimates",
    name: "Go to Estimates",
    description: "Navigate to sales & estimates",
    keys: "g+e",
    category: "navigation",
    icon: Calculator,
  },
  {
    id: "goto-clients",
    name: "Go to Clients",
    description: "Navigate to client management",
    keys: "g+c",
    category: "navigation",
    icon: Users,
  },
  {
    id: "goto-settings",
    name: "Go to Settings",
    description: "Navigate to settings",
    keys: "g+s",
    category: "navigation",
    icon: Settings,
  },

  // Action Shortcuts
  {
    id: "new-project",
    name: "New Project",
    description: "Create a new project",
    keys: "mod+shift+p",
    category: "actions",
    icon: Plus,
  },
  {
    id: "new-estimate",
    name: "New Estimate",
    description: "Create a new estimate",
    keys: "mod+shift+e",
    category: "actions",
    icon: Plus,
  },
  {
    id: "new-client",
    name: "New Client",
    description: "Create a new client",
    keys: "mod+shift+c",
    category: "actions",
    icon: Plus,
  },
  {
    id: "quick-action",
    name: "Quick Action",
    description: "Context-aware quick create",
    keys: "mod+n",
    category: "actions",
    icon: Plus,
  },
  {
    id: "ai-send-message",
    name: "Send AI message",
    description: "Send a message to Hank",
    keys: "mod+enter",
    category: "actions",
    icon: MessageSquare,
  },
  {
    id: "ai-new-conversation",
    name: "New AI conversation",
    description: "Start a new AI conversation",
    keys: "mod+shift+n",
    category: "actions",
    icon: MessageSquare,
  },

  // Search Shortcuts
  {
    id: "command-palette",
    name: "Command Palette",
    description: "Open command palette",
    keys: "mod+k",
    altKeys: "mod+p",
    category: "search",
    icon: Search,
  },
  {
    id: "search-projects",
    name: "Search Projects",
    description: "Quick search for projects",
    keys: "mod+shift+f",
    category: "search",
    icon: Search,
  },

  // Global Shortcuts
  {
    id: "toggle-sidebar",
    name: "Toggle Sidebar",
    description: "Show/hide the sidebar",
    keys: "mod+b",
    category: "global",
  },
  {
    id: "show-shortcuts",
    name: "Keyboard Shortcuts",
    description: "Show keyboard shortcuts help",
    keys: "mod+/",
    altKeys: "?",
    category: "global",
  },
];

/**
 * Shortcut categories for help modal grouping
 */
export const shortcutCategories = {
  navigation: {
    name: "Navigation",
    description: "Move between different sections",
  },
  actions: {
    name: "Actions",
    description: "Create and manage items",
  },
  search: {
    name: "Search",
    description: "Find and filter content",
  },
  global: {
    name: "Global",
    description: "Application-wide controls",
  },
} as const;
